/**
 * Applique une trame `conversation_cases` hors de tout écran : elle arrive aussi
 * pour une conversation en arrière-plan.
 *
 * WHY écrire dans le cache du détail : à la réouverture, `AgentV3View` pose le
 * dossier du composeur depuis ce détail. Resté sur l'ancien `case_id`, il ferait
 * partir un changement de principal que personne n'a demandé. Une lecture en vol
 * est annulée : partie avant la trame, elle l'écraserait en revenant.
 */

import type { Query, QueryClient } from '@tanstack/react-query';
import type { ConversationCasesPayload } from '@hector/shared-types';
import { toast } from 'sonner';
import type { Conversation } from '@/types/api';
import { decrirePerteDeLecteurs, useConversationCasesStore } from '../stores/conversationCasesStore';

function estDetailDe(conversationId: string) {
  return (query: Query): boolean => (
    query.queryKey[0] === 'conversations' && query.queryKey[1] === 'detail' && query.queryKey[4] === conversationId
  );
}

function titreEnCache(queryClient: QueryClient, conversationId: string): string | null {
  const [entree] = queryClient.getQueriesData<Conversation>({ predicate: estDetailDe(conversationId) });
  return entree?.[1]?.conversation_title ?? null;
}

/** Voir le module. */
export function appliquerLaListeDeLaConversation(queryClient: QueryClient, payload: ConversationCasesPayload): void {
  const { conversation_id: conversationId, cases, lost_readers: lostReaders } = payload;
  useConversationCasesStore.getState().publierListe(conversationId, cases, Date.now());
  const detail = { predicate: estDetailDe(conversationId) };
  void queryClient.cancelQueries(detail);
  queryClient.setQueriesData<Conversation>(detail, (actuel) => actuel && {
    ...actuel,
    case_id: cases.find((item) => item.role === 'principal')?.case_id ?? null,
    conversation_cases: cases,
  });
  if (lostReaders.length > 0) {
    // Persistant : l'avocat peut regarder une autre conversation à cet instant, et ce
    // lecteur ne verra plus rien sans que personne d'autre ne le lui dise.
    toast.warning(decrirePerteDeLecteurs(lostReaders, titreEnCache(queryClient, conversationId)), {
      id: `lecteurs-perdus:${conversationId}`,
      duration: Infinity,
      closeButton: true,
    });
  }
}
